import TableData from '@/layouts/layout-table/layout-table-data';
import TableRow from '@/layouts/layout-table/layout-table-row';
import { RegistrantResponse, UnHideRegistrations } from '@/connection/audiences/registrants';
import Checkbox from '@ui/checkbox/checkbox';
import {
  formatDateReadableLocale,
  formatTimeReadableLocale,
  getCourseCompletedState,
  registrantName,
} from '../../utils';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import { UpdateCompletedState } from '../../types';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import ProfileTableCell from '@/micros/edit-project/grid-areas/main/audiences/components/profile-table-cell';
import { ICONS } from '@ui/icons/types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { getWorkingProject, selectIsCourseEnabled } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import { selectMemberPasscodeListsLoading } from '@/store/slices/passcode-lists/selectors';
import Button from '@ui/buttons/button/button';
import toastTrigger from '@ui/toast/toast-trigger';
import Icon from '@ui/icons/icon';
import TruncatedTextList from '@ui/truncated-list/truncated-text-list';
import { useEffect, useState } from 'react';
import AddRegistrantToList from './modals/add-registrant-to-list-modal';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EBroadcastTypes } from '@/types/session.types';
import { EToastTypes } from '@ui/toast/toast.types';

type RegistrantTableRowProps = {
  registrant: RegistrantResponse;
  forceCourseCompletion?: (blProfile: number) => void;
  downloadCertificate?: (blProfile: number) => void;
  updatingUsers?: number[];
  generatingPdfForUsers?: number[];
  memberOfPasscodeLists: string[];
  isSelected?: boolean;
  setIsSelected: () => void;
  reloadRegistrants?: () => void;
  canEdit?: boolean;
};

// TODO: add commented columns

const renderStatusIcon = (registrant: RegistrantResponse) => {
  const getIconAndText = () => {
    switch (registrant.status) {
      case 'Registered':
        return { icon: ICONS.USER_REGISTERED, text: 'Registered' };
      case 'Invited':
        return { icon: ICONS.UPLOAD_V2, text: 'Invited' };
      case 'Attended':
        return { icon: ICONS.USER_REGISTERED, text: 'Attended' };
      default:
        return { icon: ICONS.USER_OUTLINE, text: registrant.status || 'N/A' };
    }
  };

  const { icon, text } = getIconAndText();

  return (
    <>
      <Icon name={icon} />
      <Text className="text-ellipsis" style={{ maxWidth: '200px' }}>
        {text}
      </Text>
    </>
  );
};

const RegistrantsTableRow = ({
  registrant,
  updatingUsers,
  generatingPdfForUsers,
  forceCourseCompletion,
  downloadCertificate,
  memberOfPasscodeLists,
  isSelected = false,
  setIsSelected,
  reloadRegistrants,
  canEdit = true,
}: RegistrantTableRowProps) => {
  const coursesEnabled = useAppSelector(selectIsCourseEnabled);
  const memberPasscodeListsLoading = useAppSelector(selectMemberPasscodeListsLoading);
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const workingProject = useAppSelector(getWorkingProject);

  // This is used to force re-render of the component once loading of the entire window is complete.
  // Removal of this will cause a single member of the list to be displayed ALWAYS instead of a comma delimited list with +n count where applicable
  const [counter, setCounter] = useState(0);
  const [showAddListModal, setShowAddListModal] = useState(false);

  useEffect(() => {
    setCounter((prev) => prev + 1);
  }, [memberOfPasscodeLists]);

  const renderDate = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    return (
      <>
        <Text>{formatDateReadableLocale(date)}</Text>
        <Text>{formatTimeReadableLocale(date)}</Text>
      </>
    );
  };

  const isFiresideOrEmbed = (): boolean => {
    if (!workingProject?.sessions) return false;
    return workingProject.sessions.some(
      (session) =>
        session.broadcast_type === EBroadcastTypes.fireside || session.broadcast_type === EBroadcastTypes.embed
    );
  };

  const handleCheckboxChange = () => {
    if (setIsSelected) {
      setIsSelected();
    }
  };

  const handleShowRegistrant = async (uuid: string) => {
    try {
      await UnHideRegistrations([uuid]);
      if (reloadRegistrants) {
        reloadRegistrants();
      }
    } catch (e) {
      console.error(e);
      toastTrigger('Error un-removing registrants', {
        description: 'Please refresh the page and try again.',
        type: EToastTypes.error,
        autoClose: 10000,
      });
    }
  };

  return (
    <>
      {/* key does not exist on uploaded registrants, so we use email as a fallback */}
      <TableRow key={registrant.uuid || registrant.email} style={{ position: 'relative' }}>
        <OptionalComponent display={canEdit}>
          <TableData style={{ width: 48 }}>
            <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
              <TooltipPortal
                disabled={!!registrant.uuid}
                tooltipContent={<Text color="var(--gray-true-dark-100)">User has not registered</Text>}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={handleCheckboxChange}
                  value={registrant.registration_id}
                  size={16}
                  selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                  unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                  disabled={!registrant.uuid}
                />
              </TooltipPortal>
            </div>
          </TableData>
        </OptionalComponent>
        <TableData width={170}>
          <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
            <Container>
              <TooltipPortal
                tooltipContent={
                  <>
                    <Text color="var(--gray-true-dark-100)">{registrantName(registrant)}</Text>
                    <Text color="var(--gray-true-dark-100)">{registrant.email}</Text>
                  </>
                }
              >
                <ProfileTableCell
                  firstName={registrant?.first_name}
                  lastName={registrant?.last_name}
                  email={registrant?.email}
                  avatar={registrant?.avatar}
                />
              </TooltipPortal>
            </Container>
          </div>
        </TableData>
        <TableData>
          <div style={{ opacity: registrant.deleted ? 0.5 : 1 }} className="flex ai-center gap-6">
            {renderStatusIcon(registrant)}
          </div>
        </TableData>

        <TableData>
          <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
            {registrant.created ? renderDate(registrant.created) : 'N/A'}
          </div>
        </TableData>
        <TableData>
          <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
            {registrant.last_login ? renderDate(registrant.last_login) : 'N/A'}
          </div>
        </TableData>
        <TableData>
          <Container opacity={registrant.deleted ? 0.5 : 1}>
            {isFiresideOrEmbed() ? 'N/A' : registrant.watchtimePercentage ? `${registrant.watchtimePercentage}%` : '-'}
          </Container>
        </TableData>
        <TableData>
          <Container className="flex ai-center gap-4" opacity={registrant.deleted ? 0.5 : 1}>
            <Icon name={ICONS.STAR} size={12} color="var(--primitives-mode-1-violet-500)" />
            <Text className="S2">
              {registrant.starScore ? parseFloat(Number(registrant.starScore).toFixed(1)) : '0'}
            </Text>
          </Container>
        </TableData>
        <TableData>
          <OptionalComponent display={memberOfPasscodeLists.length > 0}>
            <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
              <TooltipPortal
                tooltipContent={
                  <>
                    {memberOfPasscodeLists.map((list) => (
                      <Text color="var(--gray-true-dark-100)" key={list}>
                        {list}
                      </Text>
                    ))}
                  </>
                }
              >
                <Container className="" width="160px" skeletonLoader={!!memberPasscodeListsLoading}>
                  <TruncatedTextList
                    key={registrant.email + counter + memberOfPasscodeLists.join(',')}
                    items={memberOfPasscodeLists.map((member) => {
                      return { label: member, value: member, meta: member };
                    })}
                    plusMoreWidth={20}
                    width={180}
                    height={24}
                    gap={0}
                    containerStyle={{
                      pointerEvents: 'none',
                      cursor: 'pointer',
                      opacity: 1,
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1,
                    }}
                    placeholderStyles={{ width: '160px' }}
                  />
                </Container>
              </TooltipPortal>
            </div>
          </OptionalComponent>
          <OptionalComponent display={memberOfPasscodeLists.length === 0 && canEdit}>
            <Button className="flex ai-center gap-4" onClick={() => setShowAddListModal(true)}>
              <Icon name={ICONS.ADD} size={10} color={getThemeColorCSSVariable(EColorVariablesV2.primary)} />
              <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                Add to list
              </Text>
            </Button>
          </OptionalComponent>
        </TableData>
        {coursesEnabled && (
          <TableData>
            <div style={{ opacity: registrant.deleted ? 0.5 : 1 }}>
              <OptionalComponent display={!registrant.registration_id}>
                <Text>Not Registered</Text>
              </OptionalComponent>
              <OptionalComponent display={!!registrant.registration_id}>
                <Container className="flex jc-space-between ai-center gap-8">
                  <Container width="80px">
                    <Text backgroundColor="none" fontSize={10}>
                      {registrant.course_completed ? 'Passed' : 'Not Passed'}
                    </Text>
                  </Container>
                  <Container width="133px" style={{ fontSize: '12px' }}>
                    <SwitchCase input={getCourseCompletedState(registrant, updatingUsers, generatingPdfForUsers)}>
                      <Case value={UpdateCompletedState.None}>
                        <div />
                      </Case>
                      <Case value={UpdateCompletedState.Updating}>
                        <div className="completion-state updating">
                          <button className="small flex ai-center" disabled={true}>
                            <WaitingIndicator />
                          </button>
                        </div>
                      </Case>
                      <Case value={UpdateCompletedState.Passed}>
                        <div className="completion-state passed">
                          <div className="completion-state updating">
                            <button className="small flex ai-center" disabled={true}>
                              Passed
                            </button>
                          </div>
                        </div>
                      </Case>
                      <Case value={UpdateCompletedState.NotPassed}>
                        <div className="completion-state not-passed">
                          <div className="completion-state updating">
                            <button
                              className="small action flex ai-center"
                              onClick={() => forceCourseCompletion?.(registrant.bl_profile)}
                            >
                              Pass Override
                            </button>
                          </div>
                        </div>
                      </Case>
                      <Case value={UpdateCompletedState.CertificateAvailable}>
                        <div className="completion-state certificate-available">
                          <Button
                            className="padding-v-8 padding-h-16"
                            onClick={() => downloadCertificate?.(registrant.bl_profile)}
                            backgroundColor={getThemeColorCSSVariable()}
                          >
                            <Text
                              color={getThemeColorContrastCSSVariable(
                                EColorVariablesV2.buttonText,
                                EColorVariablesV2.primary
                              )}
                              fontSize="12px"
                              className="L1 padding-l-4"
                            >
                              Download
                            </Text>
                          </Button>
                        </div>
                      </Case>
                      <Case value={UpdateCompletedState.GeneratingCertificate}>
                        <div className="completion-state generating-certificate">
                          <button className="small flex ai-center" disabled={true}>
                            Generating <WaitingIndicator />
                          </button>
                        </div>
                      </Case>
                    </SwitchCase>
                  </Container>
                </Container>
              </OptionalComponent>
            </div>
          </TableData>
        )}

        <OptionalComponent display={registrant.deleted}>
          <TableData width="72px" padding="0px">
            <Container className="flex ai-center gap-12" maxWidth="134px">
              <Button
                width="auto"
                onClick={() => handleShowRegistrant(registrant.uuid)}
                disabledOpacity={0.6}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Show
                </Text>
              </Button>
            </Container>
          </TableData>
        </OptionalComponent>
      </TableRow>
      <AddRegistrantToList
        open={showAddListModal}
        setOpen={setShowAddListModal}
        passcodeLists={passcodeLists}
        registrants={[
          {
            email: registrant.email,
            first_name: registrant.first_name,
            last_name: registrant.last_name,
          },
        ]}
        onSaveComplete={reloadRegistrants}
      />
    </>
  );
};

export default RegistrantsTableRow;
